import { useRef, useState } from "react";
import { EnvelopeSimple, PaperPlaneTilt, GraduationCap, ArrowDown } from "@phosphor-icons/react";
import { toast } from "sonner";
import { createInquiry } from "@/lib/api";

const ACCENT = {
  coral: { stripe: "bg-coral", soft: "bg-coral/15", text: "text-ink", pill: "bg-coral text-white" },
  saffron: { stripe: "bg-saffron", soft: "bg-saffron/25", text: "text-ink", pill: "bg-saffron text-ink" },
  mint: { stripe: "bg-mint", soft: "bg-mint/20", text: "text-ink", pill: "bg-mint text-white" },
  teal: { stripe: "bg-teal", soft: "bg-sky/30", text: "text-ink", pill: "bg-teal text-white" },
  lilac: { stripe: "bg-lilac", soft: "bg-lilac/25", text: "text-ink", pill: "bg-lilac text-white" },
};

const inputCls =
  "w-full border border-paper-edge bg-paper-alt px-3 py-2.5 font-mono text-sm focus:outline-none focus:border-ink/40";

export const ServicesSection = ({ services = [], contactEmail = "" }) => {
  const formRef = useRef(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [service, setService] = useState(services[0]?.title || "");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  if (!services.length) return null;

  const enquire = (title) => {
    setService(title);
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await createInquiry({ name, email, phone, service, message });
      toast.success("Inquiry sent", {
        description: "Thank you — Dr Chakrabortty will reach out to you by email shortly.",
      });
      setName("");
      setEmail("");
      setPhone("");
      setMessage("");
    } catch (err) {
      toast.error("Could not send inquiry", {
        description: err?.response?.data?.detail || err.message,
      });
    } finally {
      setSending(false);
    }
  };

  const mailtoHref = contactEmail
    ? `mailto:${contactEmail}?subject=${encodeURIComponent(`Inquiry: ${service}`)}`
    : null;

  return (
    <div data-testid="services-section" className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 pt-3 pb-2">
        {services.map((s, i) => {
          const palette = ACCENT[s.accent] || ACCENT.coral;
          const tilt = i % 3 === 0 ? "sticker-tilt-l" : i % 3 === 2 ? "sticker-tilt-r" : "";
          return (
            <div
              key={s.title}
              className={`sticker p-6 relative overflow-hidden ${palette.soft} avoid-break flex flex-col ${tilt}`}
              data-testid={`service-card-${i}`}
            >
              <div className={`absolute top-0 left-0 h-3 w-full ${palette.stripe}`} />
              <div className="flex items-center justify-between gap-2 mb-4 mt-2">
                <GraduationCap size={26} weight="duotone" className="text-ink" />
                <span className={`px-2.5 py-1 text-[10px] font-mono uppercase tracking-wider border-2 border-ink ${palette.pill}`}>
                  {s.tag}
                </span>
              </div>
              <h3 className="font-chunk text-xl tracking-tight leading-snug uppercase">{s.title}</h3>
              <p className="text-sm text-ink leading-relaxed mt-2.5 flex-1">{s.description}</p>
              <button
                type="button"
                onClick={() => enquire(s.title)}
                className="mt-5 btn-pill btn-pill-ink self-start no-print"
                data-testid={`service-enquire-btn-${i}`}
              >
                <ArrowDown size={12} weight="bold" />
                Enquire
              </button>
            </div>
          );
        })}
      </div>

      <div
        ref={formRef}
        className="sticker p-6 lg:p-8 relative overflow-hidden bg-white no-print mt-3"
        data-testid="inquiry-form-card"
      >
        <div className="absolute top-0 left-0 h-3 w-full flex">
          <span className="flex-1 bg-coral" />
          <span className="flex-1 bg-saffron" />
          <span className="flex-1 bg-mint" />
          <span className="flex-1 bg-teal" />
          <span className="flex-1 bg-lilac" />
        </div>
        <div className="overline text-coral-hover mb-2 mt-3">Work with me</div>
        <h3 className="font-chunk text-2xl lg:text-3xl tracking-tight uppercase">
          Interested? <span className="hl-lime">Send an inquiry</span>
        </h3>
        <p className="text-sm text-ink-mute mt-2.5 max-w-2xl">
          Share a few details and Dr Chakrabortty will get back to you over email.
        </p>

        <form onSubmit={submit} className="mt-6 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="inq-name" className="overline mb-2 block">Your name</label>
              <input
                id="inq-name"
                data-testid="inquiry-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Full name"
                required
                className={inputCls}
              />
            </div>
            <div>
              <label htmlFor="inq-email" className="overline mb-2 block">Your email</label>
              <input
                id="inq-email"
                data-testid="inquiry-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                className={inputCls}
              />
            </div>
            <div>
              <label htmlFor="inq-phone" className="overline mb-2 block">Phone (optional)</label>
              <input
                id="inq-phone"
                data-testid="inquiry-phone-input"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+91 …"
                className={inputCls}
              />
            </div>
            <div>
              <label htmlFor="inq-service" className="overline mb-2 block">Service of interest</label>
              <select
                id="inq-service"
                data-testid="inquiry-service-select"
                value={service}
                onChange={(e) => setService(e.target.value)}
                className={inputCls}
              >
                {services.map((s) => (
                  <option key={s.title} value={s.title}>{s.title}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label htmlFor="inq-message" className="overline mb-2 block">Message</label>
            <textarea
              id="inq-message"
              data-testid="inquiry-message-input"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              placeholder="Tell me briefly about your research stage, topic area, and what support you need…"
              required
              className={`${inputCls} resize-none`}
            />
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <button
              type="submit"
              disabled={sending || !name || !email || !message}
              className="btn-pill btn-pill-pink disabled:opacity-50"
              data-testid="inquiry-submit-btn"
            >
              <PaperPlaneTilt size={14} weight="bold" />
              {sending ? "Sending…" : "Send inquiry"}
            </button>
            {mailtoHref && (
              <a
                href={mailtoHref}
                className="btn-pill"
                data-testid="inquiry-mailto-btn"
              >
                <EnvelopeSimple size={14} weight="bold" />
                Or email me directly
              </a>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default ServicesSection;
